import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildLabel } from '../build-info';

/**
 * The build stamp, bottom-left on every screen (app, sign-in, pending, Platform console): small and quiet; click it
 * to copy the full string for a defect report. "Copied" is said aloud too (polite live region).
 */
export function VersionTag() {
  const { t } = useTranslation();
  const label = buildLabel();
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(label);
      ok = true;
    } catch {
      // no clipboard API (an insecure origin, an old browser): the old way
      const area = Object.assign(document.createElement('textarea'), { value: label });
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      area.remove();
    }
    setCopied(ok ? 'ok' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="version-tag">
      <button
        type="button"
        title={t('build.copy')}
        aria-label={`${label} — ${t('build.copy')}`}
        onClick={() => void copy()}
      >
        {label}
      </button>
      {/* "Copied", beside the stamp and said aloud */}
      <span role="status" aria-live="polite">
        {copied === 'ok' ? ` ${t('build.copied')}` : copied === 'failed' ? ` ${t('build.copyFailed')}` : ''}
      </span>
    </div>
  );
}
